import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../../../infrastructure/services/api/services';
import { apiConfig } from '../../../application/configurations/apiConfig';
import { ApiConfig } from '../../../application/configurations/IConfig';
import { toggleLoading } from '../../components/pageLoader/loaderReducer';
import { appConstants } from '../../../application/configurations/constants';
import { UtilityService } from '../../../infrastructure/services/utils';
import { resultsConfigurations } from './config';
import {
  enableFlagForSuggestedOrRecentSearch,
  setBestBetFlag,
  setInsightsFlag
} from './searchReducer';
import { store } from '../../../application/store';
import AnalyticsEvents from '../../../infrastructure/services/analytics/analyticsUseCase';
import { AdobeAnalyticsEvents } from '../../../infrastructure/services/analytics/adobeAnalyticsUseCase';
import { setHomePageSearchFlag } from './searchReducer';
import { PeopleSortConfig } from '../../components/sort/config';
import { resultsConstants } from './constants';
import { cardConfig } from '../../components/cards/config';
import envConfig from '../../../application/configurations/envConfig';
import JsonData from '../../../application/configurations/refiner.json';
import HybridSearchJsonData from '../../../application/configurations/HybridSearchRefinersSequence.json';
import GeographyData from '../../../application/configurations/geographyTaxonomy.json';

/**
 * class for processing async functions based on actions
 */
class Search {
  private searchApiConfig: ApiConfig;

  private hybridSearchApiConfig: ApiConfig;

  private analyticsApiConfig: ApiConfig;

  private isSematicSearchEnabled: boolean = false;

  constructor() {
    this.searchApiConfig = { ...apiConfig.search };
    this.hybridSearchApiConfig = { ...apiConfig.HybridSearch };
    this.analyticsApiConfig = { ...apiConfig.analytics };
  }

  //Call search and hybrid search API based on wordlength - Hybrid search

  private getSearchConfig(searchTerm: string, vertical: string) {
    console.log('Debugging getSearchConfig:', { searchTerm });
    // Count the number of keywords in the search term
    const wordCount = searchTerm.trim().split(/\s+/).length;
    // Use hybrid search for 4 or more keywords
    if(searchTerm.includes(":") && searchTerm.toLowerCase().includes("esc_")){
      this.isSematicSearchEnabled = false;

      return this.searchApiConfig;
    }
    else if (
      wordCount >= envConfig.HybridSearchWordLimit &&
      envConfig.HybridSearchAllowedVerticals.length <= 0
    ) {
      this.isSematicSearchEnabled = true;

      return this.hybridSearchApiConfig;
    } else if (
      wordCount >= envConfig.HybridSearchWordLimit &&
      envConfig.HybridSearchAllowedVerticals.includes(vertical)
    ) {
      this.isSematicSearchEnabled = true;

      return this.hybridSearchApiConfig;
    } else {
      this.isSematicSearchEnabled = false;

      return this.searchApiConfig;
    }
  }

  /**
   * async method with redux store middleware Thunk wrapper
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {object} request the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  doSearch = createAsyncThunk(
    'search',
    async (
      request: {
        searchTerm: string;
        vertical: string;
        filterByDate: number;
        accounts: Array<Object>;
        refinersClicked: any;
        flagForContentAge: boolean;
        flagForClear: boolean;
        startRow: number;
        homepageSearch: boolean;
        sortByValue: {
          name: string;
          Value: string;
          Direction: number;
          SortType: number;
        };
        refinerValues: any;
        clearAll: boolean;
      },
      thunkApi
    ) => {
      // dispatching action to show loader
      thunkApi.dispatch(toggleLoading(true));
      let responseData: any = [];
      let resultCount: number = 0;
      let peopleAccountId: Array<string | undefined> = [];
      let profilePhotoCollection: any = [];
      let refinedVerticals: any = [];
      let latency: number = 0;
      let nestedRefinersFinal: any = [];
      const filterObject: any = JsonData.filter(
        (item) => item.vertical === request.vertical
      );
      const HybridSearchSequence: any = HybridSearchJsonData.filter(
        (item) => item.vertical === request.vertical
      )[0];
      let resultsLimit: any = resultsConfigurations.resultsPerPage.filter(
        (option) => option.vertical === request.vertical
      );
      let noOfItemsPerPage = JSON.parse(
        localStorage.getItem(appConstants.noOfItemsPerPage) || '{}'
      );
      let itemsToSetPerPage: number = noOfItemsPerPage
        ? noOfItemsPerPage
        : appConstants.defaultItemsPerPage;
      let modelObj = UtilityService.getRequestModel(request.vertical);
      const searchConfig = {
        ...this.getSearchConfig(request.searchTerm, request.vertical)
      };
      searchConfig.accountInfo = request.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: request.searchTerm,
        verticals: request.vertical,
        Refiners:
          filterObject && filterObject.length > 0
            ? Object.keys(filterObject[0])
            : [appConstants.RefinerName],
        Size:
          request.startRow > resultsLimit[0].count - itemsToSetPerPage
            ? resultsLimit[0].count - request.startRow
            : itemsToSetPerPage,
        From: request.startRow ? request.startRow : 0,
        ReturnFields: modelObj ? Object.keys(modelObj) : [],
        Sort: []
      };
      // Initialize Filters
      searchConfig.data.Filters = {
        Condition: 'AND',
        Rules: []
      };
      // filter search by Date of content age API call condition
      if (request.filterByDate && modelObj.contentAge) {
        searchConfig.data.Filters.Rules.push({
          Field: modelObj.contentAge,
          Type: 'Date',
          Operator: 'Range',
          Value: `${UtilityService.getMonthDates(
            request.filterByDate
          )};${new Date().toISOString()}`
        });
      }
       if (request.vertical === appConstants.verticals.All || request.vertical === appConstants.verticals.News) {
        searchConfig.data.PersonalFilters = {
        Condition: 'AND',
        Rules: []
        };
       
          let geographyFilters: string[] = [];
          const userInfo = JSON.parse(sessionStorage.UserInfo);
          if (userInfo.EYCountry) {
            const findGeographyPath = (
              nodes: any[],
              countryName: string,
              path: string[]
            ): string[] | null => {
              for (const node of nodes) {
                const currentPath = [...path, node.label];
                if (
                  node.label === countryName ||
                  (node.altLabels && node.altLabels.includes(countryName))
                ) {
                  return currentPath;
                }
                if (node.children) {
                  const result = findGeographyPath(
                    node.children,
                    countryName,
                    currentPath.reverse()
                  );
                  if (result) {
                    return result;
                  }
                }
              }

              return null;
              
            };

            const path = findGeographyPath(
              GeographyData.children,
              userInfo.EYCountry,
              []
            );

            if (path) {
              geographyFilters.push(...path);
            } else {

              geographyFilters.push(userInfo.EYCountry);
            }
          }
     
        geographyFilters.push('Global');
        searchConfig.data.PersonalFilters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY',
          Type: 'String',
          Operator: ':',
          Value: geographyFilters.toLocaleString() //JSON.stringify(geographyFilters)
        });
        searchConfig.data.PersonalFilters.Rules.push({
          Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
          Type: 'String',
          Operator: ':',
          Value: geographyFilters.toLocaleString() //JSON.stringify(geographyFilters)
        });
      }

      // Refiners API call condition
      if (
        request.refinerValues !== undefined &&
        request.refinerValues.length > 0
      ) {
        if (request.vertical === appConstants.verticals.People) {
          searchConfig.data.Filters = {
            Condition: 'AND',
            Rules: []
          };
        }
        request.refinerValues.forEach((item: any) => {
          if (
            item[Object.keys(item)[0]] !== undefined &&
            item[Object.keys(item)[0]].length > 0
          ) {
            // Condition to check if selected refiner is more than one value
            UtilityService.selectedRefiners(
              item,
              searchConfig.data.Filters.Rules
            );
          }
        });
      }

      //  sort by filter API call condition
      if (request.sortByValue && request.sortByValue !== undefined) {
        searchConfig.data.Sort.push({
          FieldName: request.sortByValue.Value,
          Ascending: request.sortByValue.Direction == 1 ? false : true
        });
        if (
          request.vertical === appConstants.verticals.People &&
          (request.sortByValue.Value ===
            PeopleSortConfig.filterOptions[1].Value ||
            request.sortByValue.Value ===
              PeopleSortConfig.filterOptions[3].Value)
        ) {
          searchConfig.data.Sort.push({
            FieldName:
              request.sortByValue.Value ===
              PeopleSortConfig.filterOptions[1].Value
                ? PeopleSortConfig.filterOptions[3].Value
                : PeopleSortConfig.filterOptions[1].Value,
            Ascending: request.sortByValue.Direction == 1 ? false : true
          });
        }
      }
      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            // Variable for backend failure counts i.e., isError property count in the actual API response object
            let errorCount: number = 0;

            if (response && response[0] && response[0].result.relevantResults) {
              try {
                // Refiners
                // First Level
                let level1Transformation = UtilityService.convertRefinersLevel1(
                  JSON.parse(response[0].result.refinementResultsJSON)
                );

                // Second Level
                level1Transformation.map((refine: any) => {
                  let object1: any = {};
                  //Condition to check if the Refiner is from AEM using path keyword.
                  object1 =
                    refine.title.indexOf('UFPATH') > -1
                      ? {
                          title: refine.title,
                          refId: refine.refId,
                          ID: this.isSematicSearchEnabled
                            ? Object.keys(HybridSearchSequence).find(
                                (k) => HybridSearchSequence[k] === refine.title
                              )
                            : refine.ID,
                          source: UtilityService.convertRefinersLevel2({
                            RefinementResults: refine.source.slice(1)
                          })
                        }
                      : {
                          title: refine.title,
                          refId: refine.refId,
                          ID: this.isSematicSearchEnabled
                            ? Object.keys(HybridSearchSequence).find(
                                (k) => HybridSearchSequence[k] === refine.title
                              )
                            : refine.ID,
                          source: UtilityService.convertRefinersLevel2({
                            RefinementResults: refine.source
                          })
                        };

                  object1.title =
                    filterObject[0] &&
                    filterObject[0][object1.title] !== undefined &&
                    object1.title !== 'vertical'
                      ? filterObject[0][object1.title]
                      : object1.title;

                  nestedRefinersFinal.push(object1);
                });

                // Dont remove this console until we implement Refiners.
                if (this.isSematicSearchEnabled) {
                  // nestedRefinersFinal = [...nestedRefinersFinal].sort((first,next)=>first.ID-next.ID)
                  nestedRefinersFinal = [...nestedRefinersFinal].sort(
                    (a, b) => {

                      if (a.ID === undefined) return 1; // Place undefined at the end

                      if (b.ID === undefined) return -1; // Place undefined at the end

                      return a.ID - b.ID; // Sort numerically
                    }
                  );
                }
                console.log(nestedRefinersFinal);
              } catch (error) {
                console.log(error);
              }
              // Refiners logics ends here.

              response[0].result.relevantResults.forEach((record: any) => {
                let federatorData = JSON.parse(record.FederatorMetadata);
                if (federatorData && federatorData.length > 0) {
                  let backendName = federatorData.find((obj: any) => {
                    return obj.Key === 'FederatorBackend';
                  }).Value;
                  switch (backendName) {
                    case appConstants.backendDataSources.people:
                      record.vertical = appConstants.verticals.People;
                      peopleAccountId.push(record.accountId);
                      break;
                    case appConstants.backendDataSources.people_stg:
                      record.vertical = appConstants.verticals.People;
                      peopleAccountId.push(record.accountId);
                      break;
                    default:
                      break;
                  }

                  responseData.push(
                    UtilityService.getResponseModel(record.vertical, record)
                  );
                } else {
                  // Increasing the backend failure count when the FederatorMetadata array is empty in the results.
                  errorCount++;
                }
              });
              // Setting up the Results Found count by decreasing with the backend failure count(errorCount).
              // This will fix for the backend failure scenarios i.e., when the backend returns any error in the response.
              resultCount =
                responseData.length > 0
                  ? response[0].result.totalRelevantResults - errorCount
                  : responseData.length;
              refinedVerticals = response[0].result.refinementResults;
              latency = response[0].result.totalTime;
              if (peopleAccountId.length) {
                thunkApi.dispatch(
                  this.getUserProfilePic({
                    peopleAccountIds: peopleAccountId,
                    accounts: request.accounts
                  })
                );
              }
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          // Calling the Analytics Events
          this.callAnalytics(request, resultCount);

          // injecting page load adobe Analytics Events
          AdobeAnalyticsEvents.injectAdobePageLoadEvents(
            request.searchTerm,
            resultCount,
            store.getState().general.userInfo,
            request.vertical,
            false
          );

          thunkApi.dispatch(setHomePageSearchFlag(false));
          thunkApi.dispatch(enableFlagForSuggestedOrRecentSearch(''));
        });

      return {
        results: responseData,
        resultCount: resultCount,
        profilePhotoCollection: profilePhotoCollection,
        refiners: refinedVerticals,
        latency: latency,
        nestedRefiners: nestedRefinersFinal
      };
    }
  );

  /**
   * Method to trigger analytics actions
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * @param  {object} request the request object containing search query term & an optional start row.
   * @param  {number} resultCount search result count
   */
  callAnalytics = (
    request: {
      searchTerm: string;
      vertical: string;
      filterByDate?: number;
      accounts: Array<Object>;
      startRow?: number | undefined;
      homepageSearch: boolean;
      refinersClicked?: Array<Object>;
      flagForContentAge?: boolean;
      flagForClear?: boolean;
      clearAll?: boolean;
    },
    resultCount: number | undefined
  ) => {
    const flagForSuggestedOrRecentSearch =
      store.getState().search.flagForSuggestedOrRecentSearch;

    if (flagForSuggestedOrRecentSearch !== '') {
      const wasRecentSearch =
        flagForSuggestedOrRecentSearch ===
        resultsConfigurations.RecentSearchClick
          ? 'true'
          : 'false';
      // Calling the RecentSearch and Suggestion Click analytics usecase
      AnalyticsEvents.recentSearchAndSuggestionsClick(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        wasRecentSearch,
        resultCount
      );
    } else {
      // Calling the Search Action analytics usecase
      AnalyticsEvents.searchAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.vertical,
        resultCount
      );
    }
    if (
      request.vertical !== appConstants.verticals.People &&
      !request.clearAll
    ) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount
      );
    } else if (
      request.vertical === appConstants.verticals.People &&
      request.refinersClicked &&
      request.refinersClicked.length > 0 &&
      !request.clearAll
    ) {
      AnalyticsEvents.refinerAction(
        request.searchTerm,
        request.accounts,
        request.homepageSearch,
        request.filterByDate,
        request.refinersClicked,
        request.flagForContentAge,
        request.flagForClear,
        resultCount
      );
    }
  };

  /**
   * Method to get best bets results
   * @param   {object} requestParams the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  bestbets = createAsyncThunk(
    'bestBets',
    async (
      requestParams: {
        searchTerm: string;
        vertical: string;
        accounts: Array<Object>;
      },
      thunkApi
    ): Promise<any> => {
      let responseData: any = [];
      let modelObj = UtilityService.getRequestModel(requestParams.vertical);
      const searchConfig = { ...this.searchApiConfig };
      searchConfig.accountInfo = requestParams.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: requestParams.searchTerm,
        verticals: requestParams.vertical,
        Size: 3,
        Filters: {
          Condition: 'AND',
          Rules: []
        },
        ReturnFields: modelObj ? Object.keys(modelObj) : []
      };
      let geographyFiltersBestBets: string[] = [];
      const userInfo = JSON.parse(sessionStorage.UserInfo);
      if (userInfo.EYCountry) {
        const findGeographyPath = (
          nodes: any[],
          countryName: string,
          path: string[]
        ): string[] | null => {
          for (const node of nodes) {
            const currentPath = [...path, node.label];
            if (
              node.label === countryName ||
              (node.altLabels && node.altLabels.includes(countryName))
            ) {
              return currentPath;
            }
            if (node.children) {
              const result = findGeographyPath(
                node.children,
                countryName,
                currentPath
              );
              if (result) {
                return result;
              }
            }
          }

          return null;
        };

        const path = findGeographyPath(
          GeographyData.children,
          userInfo.EYCountry,
          []
        );

        if (path) {
          geographyFiltersBestBets.push(...path.reverse());
        } else {
          geographyFiltersBestBets.push(userInfo.EYCountry);
          }
          if (geographyFiltersBestBets.indexOf(userInfo.EYCountry) === -1) {
              geographyFiltersBestBets.push(userInfo.EYCountry);
          }
      }
      geographyFiltersBestBets.push('Global');
      searchConfig.data.Filters.Rules.push({
        Field: 'ESC_GEOGRAPHICAPPLICABILITY',
        Type: 'String',
        Operator: ':',
        Value: geographyFiltersBestBets.toLocaleString()
      });
      searchConfig.data.Filters.Rules.push({
        Field: 'ESC_GEOGRAPHICAPPLICABILITY_ALT_LABEL',
        Type: 'String',
        Operator: ':',
        Value: geographyFiltersBestBets.toLocaleString()
      });
      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            if (response && response[0] && response[0].result.relevantResults) {
              response[0].result.relevantResults.forEach((record: any) => {
                let federatorData = JSON.parse(record.FederatorMetadata);
                if (federatorData && federatorData.length > 0) {
                  let backendName = federatorData.find((obj: any) => {
                    return obj.Key === 'FederatorBackend';
                  }).Value;
                  if (
                    backendName === appConstants.backendDataSources.bestbets ||
                    backendName === appConstants.backendDataSources.bestbets_stg
                  ) {
                    record.vertical = appConstants.BestBets;
                  }
                  responseData.push(
                    UtilityService.getResponseModel(record.vertical, record)
                  );
                }
              });
            }
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          if (responseData && responseData.length > 0) {
            thunkApi.dispatch(setBestBetFlag(true));
          } else {
            thunkApi.dispatch(setBestBetFlag(false));
          }
        });

      return {
        bestBetsResults: responseData.sort((a: any, b: any) =>
          a.Title.localeCompare(b.Title)
        )
      };
    }
  );

  /**
   * Method to get Insight results
   * @param   {object} requestParams the request object containing search query term & an optional start row.
   * @return  {object} return api response
   */
  insights = createAsyncThunk(
    'insights',
    async (
      requestParams: {
        searchTerm: string;
        vertical: string;
        accounts: Array<Object>;
      },
      thunkApi
    ): Promise<any> => {
      let responseData: any = [];
      let modelObj = UtilityService.getRequestModel(requestParams.vertical);
      const searchConfig = { ...this.searchApiConfig };
      searchConfig.accountInfo = requestParams.accounts;
      searchConfig.data = {
        ...searchConfig.data,
        Query: `ESC_ENTERPRISEKEYWORDS.keyword:"${requestParams.searchTerm}"`,
        verticals: 'Documents',
        Filters: {
          Condition: 'OR',
          Rules: [
            {
              Field: 'contentType',
              Type: 'String',
              Operator: ':',
              Values: [
                'EY Knowledge Buyer Page',
                'EY Knowledge FoP Page',
                'EY Knowledge Solution Page',
                'EY Topic Page'
              ]
            }
          ]
        },
        Size: 1,
        ReturnFields: modelObj ? Object.keys(modelObj) : []
      };

      await ApiService.requestApi([searchConfig])
        .then(
          (response: { [key: string]: any }) => {
            if (response && response[0] && response[0].result.relevantResults) {
              response[0].result.relevantResults.forEach((record: any) => {
                responseData.push(
                  UtilityService.getResponseModel(record.vertical, record)
                );
              });
            }

            let peopleEmailIds: Array<string | undefined> = [];
            let contentType: string =
              response[0] && responseData[0].contentType;

            switch (contentType) {
              case cardConfig.contentTypes.EYKnowledgeFoPPage:
              case cardConfig.contentTypes.EYKnowledgeSolutionPage:
                peopleEmailIds =
                  responseData[0].solutionLeadersEmail.split(';');
                break;

              case cardConfig.contentTypes.EYKnowledgeBuyerPage:
              case cardConfig.contentTypes.EYTopicPage:
                peopleEmailIds = responseData[0].eyContactEmail.split(';');
                break;

              default:
                break;
            }

            // Graph API call for Insights leads data fetch
            thunkApi.dispatch(
              this.getInsightLeadsOrPhotoFromGraph({
                peopleEmails: peopleEmailIds,
                accounts: requestParams.accounts,
                isPhoto: false
              })
            );

            thunkApi.dispatch(
              this.getInsightLeadsOrPhotoFromGraph({
                peopleEmails: peopleEmailIds,
                accounts: requestParams.accounts,
                isPhoto: true
              })
            );
          },
          (error) => {
            throw new Error(error);
          }
        )
        .finally(() => {
          if (responseData && responseData.length > 0) {
            thunkApi.dispatch(setInsightsFlag(true));
          } else {
            thunkApi.dispatch(setInsightsFlag(false));
          }
        });

      return {
        insightResults: responseData
      };
    }
  );

  /**
   * Method to get Insight Leads results from graph API
   * @param   {object} requestParams the request object containing leadsemails.
   * @return  {object} return api response
   */
  public getInsightLeadsOrPhotoFromGraph = createAsyncThunk(
    'fetchLeads',
    async (requestParams: {
      peopleEmails: Array<string | undefined>;
      accounts: Array<Object>;
      isPhoto: boolean;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleEmails.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountEmails = requestParams.peopleEmails.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let graphApiConfig = apiConfig.getProfilePicture;
        let insightsLeadBatchCallObject = requestParams.isPhoto
          ? this.getLeadsProfilePhotoBatchCol(chunkPeopleAccountEmails)
          : this.getLeadProfileBatchCol(chunkPeopleAccountEmails);
        graphApiConfig.data = insightsLeadBatchCallObject;
        graphApiConfig.accountInfo = requestParams.accounts;
        graphApiConfigArr.push(Object.assign({}, graphApiConfig));
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  getLeadProfileBatchCol = (leadEmails: any) => {
    const postDataPhoto = leadEmails.map((leadEmail: string) => ({
      id: leadEmail,
      method: 'GET',
      url: `/users/${leadEmail}?$select=displayName,mail,jobTitle,id`
    }));

    return { requests: postDataPhoto };
  };

  getLeadsProfilePhotoBatchCol = (leadEmails: any) => {
    const postDataPhoto = leadEmails.map((leadEmail: string) => ({
      id: leadEmail,
      method: 'GET',
      url: `/users/${leadEmail}/photo/$value`
    }));

    return { requests: postDataPhoto };
  };

  public getUserProfilePic = createAsyncThunk(
    'userProfilePhotos',
    async (requestParams: {
      peopleAccountIds: Array<string | undefined>;
      accounts: Array<Object>;
    }): Promise<any> => {
      let graphApiConfigArr: any = [];
      const batchSize = 20;
      for (
        let batchIndex = 0;
        batchIndex < requestParams.peopleAccountIds.length;
        batchIndex += batchSize
      ) {
        const chunkPeopleAccountIds = requestParams.peopleAccountIds.slice(
          batchIndex,
          batchIndex + batchSize
        );
        let graphApiConfig = apiConfig.getProfilePicture;

        let peopleProfileBatchCallObject = this.getPeopleProfileBatchCol(
          chunkPeopleAccountIds
        );
        graphApiConfig.data = peopleProfileBatchCallObject;
        graphApiConfig.accountInfo = requestParams.accounts;
        graphApiConfigArr.push(Object.assign({}, graphApiConfig));
      }

      return ApiService.requestApi(graphApiConfigArr);
    }
  );

  getPeopleProfileBatchCol = (peopleAccountIds: any) => {
    const postDataPhoto = peopleAccountIds.map((peopleAccountId: string) => ({
      id: peopleAccountId,
      method: 'GET',
      url: `/users/${peopleAccountId}/photo/$value`
    }));

    return { requests: postDataPhoto };
  };

  /**
   * async method to trigger suggestions search action
   * this is used to access the action based api configuration and
   * add dynamic params then send to http request service.
   * this is also pass the response to
   * @param   {string} searchTerm  search query term
   */
  queryRecommondations = createAsyncThunk(
    'queryRecommondations',
    async (request: { searchTerms: string; visibleSuggestions: number }) => {
      const suggestionsConfig = { ...apiConfig.querySuggestions };
      suggestionsConfig.data.Query = request.searchTerms;
      let responseData: any = [];
      await ApiService.requestApi([suggestionsConfig])
        .then((suggestionsArray: any) => {
          if (suggestionsArray.length > 0) {
            suggestionsArray[0].map((item: any) => {
              responseData.push(item);
            });
          }
          if (responseData.length > 0) {
            responseData.push(`Search for ${request.searchTerms}`);
          }
        })
        .catch((error) => {
          console.log('Error' + error);
        });

      return {
        suggestionList: responseData
      };
    }
  );
  /** 
   * Method to fetch the Ontology Response 
   * We are using the Ontology API to fetch the Ontology Response and parsing through it to obtain all
   the object labels and storing it in an array.
   *  @param   {object} request the request object containing search query term */

  fetchOntologyData = createAsyncThunk(
    'fetchOntologyData',
    async (request: { searchTerm: string }) => {
      const ontologyConfig = apiConfig.getOntologyData;
      ontologyConfig.baseUrl = envConfig.ontology.BaseUrl;
      ontologyConfig.data.searchText = request.searchTerm;
      const ontologyResponse: any = await ApiService.requestApi([
        ontologyConfig
      ]);

      let objectLabelsArray: any[] = [];

      ontologyResponse.map((result: any) => {
        result.result.concepts.map((concept: any) => {
          if (concept.label && concept.label.value) {
            objectLabelsArray.push(concept.label.value);
          }
        });
      });

      return {
        ontologyList: objectLabelsArray
      };
    }
  );

  /**
   * Method to trigger API endpoint to get Preview Stats
   * this is used to access the preview stats based on api endpoint configuration and
   * add dynamic params in the same fetch.
   * @param  {string} aemId AEM Id to get the Preview Stats for specific item
   */
  public getPreviewStats = createAsyncThunk(
    'getPreviewStats',
    async (request: { aemId: string }) => {
      const userDetails = store.getState().general;
      let responseData: any = [];

      await fetch(
        envConfig.aemPreviewStats.replaceAll('$eyId', request.aemId),
        {
          headers: {
            Authorization: 'Bearer ' + userDetails.token
          }
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error('Request failed');
          }

          return response.json();
        })
        .then((data) => {
          responseData = [...responseData, data];
        })
        .catch((error) => {
          console.log('Error in Preview Stats: ' + error);
        });

      return {
        previewStats: responseData[0]
      };
    }
  );
}

const SearchUseCase = new Search();
export default SearchUseCase;
